import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Typography,
  Row,
  Col,
  Avatar,
  App,
  Space,
  Divider,
  Tabs,
  Spin,
} from 'antd';
import {
  UserOutlined,
  SaveOutlined,
  MailOutlined,
  CalendarOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { changeLanguage } from '../i18n';
import { apiService } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { UserRole } from '../types/enums';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import 'dayjs/locale/en';
import 'dayjs/locale/ro';

const { Title } = Typography;
const { Option } = Select;

interface UserProfile {
  id: string;
  email: string;
  role: string;
  lastLogin?: string;
  createdAt: string;
  preferences?: {
    language?: string;
    theme?: 'light' | 'dark' | 'auto';
    timezone?: string;
  };
}

export const Profile: React.FC = () => {
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const { mode: currentTheme, setTheme, isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);

  const [userProfile, setUserProfile] = useState<UserProfile>({
    id: user?.id || '',
    email: user?.email || '',
    role: user?.role || UserRole.USER,
    lastLogin: undefined,
    createdAt: new Date().toISOString(),
    preferences: {
      language: i18n.language,
      theme: currentTheme,
      timezone: 'Europe/Kiev',
    },
  });

  useEffect(() => {
    loadProfile();
  }, []);

  // Функція для перевірки змін
  const checkForChanges = () => {
    const formValues = profileForm.getFieldsValue();
    const currentPrefs = userProfile?.preferences || {};
    
    // name та email керуються через Keycloak, тому перевіряємо тільки preferences
    const hasLanguageChange = formValues.language !== currentPrefs.language;
    const hasThemeChange = formValues.theme !== currentPrefs.theme;
    const hasTimezoneChange = formValues.timezone !== currentPrefs.timezone;
    
    const hasAnyChange = hasLanguageChange || hasThemeChange || hasTimezoneChange;
    
    setHasChanges(hasAnyChange);
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProfile();
      setUserProfile(response);
      
      // Заповнити форму
      profileForm.setFieldsValue({
        email: response.email,
        role: response.role,
        lastLogin: response.lastLoginAt,
        createdAt: response.createdAt,
        language: response.preferences?.language,
        theme: response.preferences?.theme || currentTheme,
        timezone: response.preferences?.timezone,
      });
      
      // Перевірити зміни після завантаження
      setTimeout(() => checkForChanges(), 100);
    } catch (error) {
      // Якщо помилка завантаження профілю, заповнюємо форму з поточних даних
      profileForm.setFieldsValue({
        email: user?.email || '',
        role: user?.role || UserRole.USER,
        language: i18n.language,
        theme: currentTheme,
        timezone: 'Europe/Kiev',
      });
      console.error('Profile load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values: any) => {
    try {
      setLoading(true);
      
      // Формуємо об'єкт тільки зі зміненими полями
      // name та email керуються через Keycloak, тому оновлюємо тільки preferences
      const updateData: any = {};
      const currentPrefs = userProfile?.preferences || {};
      
      // Перевіряємо чи змінилися preferences
      const newPrefs = {
        language: values.language,
        theme: values.theme,
        timezone: values.timezone,
      };
      
      if (values.language !== currentPrefs.language ||
          values.theme !== currentPrefs.theme ||
          values.timezone !== currentPrefs.timezone) {
        updateData.preferences = newPrefs;
      }
      
      // Якщо немає змін, просто повертаємось
      if (Object.keys(updateData).length === 0) {
        message.success(t('profile.noChanges'));
        return;
      }
      
			const updatedProfile = await apiService.updateProfile(updateData);
      
      setUserProfile(updatedProfile);

      // Оновити форму з новими даними
      profileForm.setFieldsValue({
        email: updatedProfile.user.email,
        role: updatedProfile.user.role,
        language: updatedProfile.user.preferences?.language,
        theme: updatedProfile.user.preferences?.theme,
        timezone: updatedProfile.user.preferences?.timezone,
      });

      // Змінити мову інтерфейсу
      if (values.language !== i18n.language) {
        changeLanguage(values.language);
      }

      // Змінити тему
      if (values.theme !== currentTheme) {
        setTheme(values.theme);
      }

      message.success(t('profile.profileUpdated'));
      setHasChanges(false);
    } catch (error) {
			message.error(t('profile.updateError'));
    } finally {
      setLoading(false);
    }
  };

  // Метод handleChangePassword видалено - паролі керуються через Keycloak

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'uk' ? 'uk' : 'en';
    return dayjs(dateString).locale(locale).format('DD.MM.YYYY HH:mm');
  };

  const getRoleText = (role: string) => {
    return role === UserRole.ADMIN ? t('roles.admin') : t('roles.user');
  };

  const tabItems = [
    {
      key: 'profile',
      label: t('profile.personalInfo'),
      children: (
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleUpdateProfile}
          initialValues={userProfile}
          onValuesChange={checkForChanges}
        >
          <Row gutter={24}>
            <Col span={24}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Avatar size={80} icon={<UserOutlined />} />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('profile.email')}
                name="email"
              >
                <Input 
                  prefix={<MailOutlined />} 
                  value={userProfile.email}
                  disabled
                  style={{ backgroundColor: isDark ? '#262626' : '#f5f5f5' }}
                />
                <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                  {t('profile.emailManagedByKeycloak') || 'Email керується через Keycloak'}
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('profile.role')}
                name="role"
              >
                <Input
                  prefix={<UserOutlined />}
                  value={getRoleText(userProfile.role)}
                  disabled
                  style={{ backgroundColor: isDark ? '#262626' : '#f5f5f5' }}
                />
                <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                  {t('profile.roleManagedByKeycloak') || 'Роль керується через Keycloak'}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('profile.language')}
                name="language"
              >
                <Select>
                  <Option value="uk">🇺🇦 Українська</Option>
                  <Option value="en">🇬🇧 English</Option>
                  <Option value="ro">🇷🇴 Română</Option>
                  <Option value="be">🇧🇾 Беларуская</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('profile.lastLogin')}
                name="lastLogin"
              >
                <Input
                  prefix={<CalendarOutlined />}
                  value={userProfile.lastLogin ? formatDate(userProfile.lastLogin) : t('profile.never') || 'Ніколи'}
                  disabled
                  style={{ backgroundColor: isDark ? '#262626' : '#f5f5f5' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('profile.createdAt')}
                name="createdAt"
              >
                <Input
                  prefix={<CalendarOutlined />}
                  value={formatDate(userProfile.createdAt)}
                  disabled
                  style={{ backgroundColor: isDark ? '#262626' : '#f5f5f5' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">{t('profile.interfaceSettings')}</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('profile.theme')}
                name="theme"
              >
                <Select>
                  <Option value="light">
                    <Space>
                      <BgColorsOutlined />
                      {t('profile.themeLight')}
                    </Space>
                  </Option>
                  <Option value="dark">
                    <Space>
                      <BgColorsOutlined />
                      {t('profile.themeDark')}
                    </Space>
                  </Option>
                  <Option value="auto">
                    <Space>
                      <BgColorsOutlined />
                      {t('profile.themeAuto')}
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('profile.timezone')}
                name="timezone"
              >
                <Select>
                  <Option value="Europe/Kiev">Europe/Kiev (GMT+2)</Option>
                  <Option value="Europe/London">Europe/London (GMT+0)</Option>
                  <Option value="Europe/Berlin">Europe/Berlin (GMT+1)</Option>
                  <Option value="America/New_York">America/New_York (GMT-5)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!hasChanges}
              icon={<SaveOutlined />}
            >
              {t('profile.save')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        {t('profile.title')}
      </Title>

      <Card>
        <Spin spinning={loading}>
          <Tabs
            defaultActiveKey="profile"
            items={tabItems}
            size="large"
          />
        </Spin>
      </Card>
    </div>
  );
}; 